<script setup lang="ts">
/**
 * La cornice del pannello: barra laterale, barra superiore, contenuto.
 *
 * Due decisioni che vale la pena giustificare, perché sono quelle che si
 * rimpiangono se prese al contrario.
 *
 * **La barra laterale scorre da sola.** Non è la pagina a scorrere con dentro
 * il menu: sono due aree indipendenti, ognuna con la propria barra. In un
 * pannello con venti voci e tabelle da cinquecento righe, un menu che scorre
 * via insieme al contenuto obbliga a risalire in cima ogni volta che si vuole
 * cambiare sezione — e a quel punto si cambia sezione di meno.
 *
 * **La larghezza si annuncia con una variabile.** `--barra` sta sulla cornice,
 * non sui singoli figli: cambia in un punto solo quando la barra si riduce, e
 * la transizione la fa il browser su una proprietà sola invece che su tre
 * regole in tre file diversi.
 */

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import AppSidebar from './AppSidebar.vue';
import AppTopbar from './AppTopbar.vue';
import AvvisoChiamataAltrove from './AvvisoChiamataAltrove.vue';
import AvvisoVersioneNuova from './AvvisoVersioneNuova.vue';
import ChiamataInArrivo from './ChiamataInArrivo.vue';
import { useAuthStore } from '@/stores/auth';
import { useUiStore } from '@/stores/ui';

const ui = useUiStore();
const auth = useAuthStore();
const route = useRoute();

const larghezzaBarra = computed(() =>
  ui.barraRidotta
    ? 'var(--beste-layout-sidebar-collapsed)'
    : 'var(--beste-layout-sidebar-expanded)',
);

const cornice = ref<HTMLElement | null>(null);
const contenuto = ref<HTMLElement | null>(null);
const colonna = ref<HTMLElement | null>(null);

/**
 * Cambiare pagina chiude il pannello mobile.
 *
 * Senza, si tocca una voce sul telefono e la schermata cambia **dietro** un
 * menu che resta aperto: sembra che il tocco non abbia funzionato, e si tocca
 * di nuovo.
 */
watch(
  () => route.fullPath,
  () => {
    ui.barraMobileAperta = false;
  },
);

/**
 * Dopo il cambio di pagina il punto di lettura torna al contenuto.
 *
 * In un'applicazione a pagina singola l'indirizzo cambia ma il documento no:
 * chi naviga da tastiera resta con il focus sulla voce di menu appena premuta,
 * e chi usa un lettore di schermo non sente annunciare niente — la nuova
 * schermata e' arrivata e non gliel'ha detto nessuno. Il risultato e' che per
 * arrivare al primo comando della pagina bisogna riattraversare tutto il menu,
 * ogni volta.
 *
 * `<main tabindex="-1">` esiste gia' nel modello proprio per questo: e'
 * raggiungibile da codice e non dal tabulatore.
 *
 * ## Perche' non un `watch` sulla rotta con `nextTick`
 *
 * Era cosi', e il commento diceva che il `nextTick` «aspetta che la transizione
 * `out-in` abbia montato il nuovo componente». Con `mode="out-in"` non e' vero:
 * il figlio nuovo entra **alla fine** della transizione d'uscita, cioe' un paio
 * di decimi di secondo dopo. Al momento del `focus()` il `<main>` ospitava
 * ancora la pagina **precedente**.
 *
 * Il beneficio da tastiera funzionava lo stesso — il contenitore e' quello, e da
 * li' si tabula in avanti — ma il beneficio dichiarato no: chi usa un lettore di
 * schermo si sentiva annunciare il contenuto della schermata che stava
 * lasciando, o niente. Cioe' proprio la meta' per cui il codice esisteva.
 *
 * `@after-enter` e' l'evento che dice davvero «adesso c'e'». Con esso sparisce
 * anche il flag `primaNavigazione`: gli hook d'entrata non scattano al primo
 * rendering, quindi l'apertura del pannello non sposta il focus da sola.
 */
function suPaginaEntrata(): void {
  contenuto.value?.focus({ preventScroll: true });
}

/**
 * Il pannello mobile e' una sovrapposizione modale, e va trattata come tale.
 *
 * Sono quattro obblighi che vanno insieme o non servono: il focus non deve
 * uscire dal pannello, quello che c'e' sotto non deve essere raggiungibile,
 * la pagina dietro non deve scorrere, e alla chiusura il focus deve tornare da
 * dove era partito. Mancandone anche uno solo, chi naviga da tastiera finisce
 * a tabulare dentro una schermata che non vede piu' — che e' peggio che non
 * avere il pannello.
 *
 * `inert` fa da solo i primi due per tutto il resto della cornice: toglie
 * l'intero sottoalbero al tabulatore, ai clic e all'albero di accessibilita'.
 * E' sostenuto ovunque da tempo e sostituisce le duecento righe di trappola
 * scritta a mano che si trovano nella maggior parte dei progetti.
 */
let focusDaRipristinare: HTMLElement | null = null;

watch(
  () => ui.barraMobileAperta,
  async (aperta) => {
    const barra = cornice.value?.querySelector<HTMLElement>('.barra');

    if (aperta) {
      focusDaRipristinare = document.activeElement as HTMLElement | null;
      if (colonna.value) colonna.value.inert = true;
      document.body.style.overflow = 'hidden';
      await nextTick();
      // Il primo comando del pannello, non il pannello: chi apre il menu vuole
      // scegliere una voce, e cosi' la prima pressione del tabulatore lo porta
      // alla seconda invece che alla prima.
      barra?.querySelector<HTMLElement>('a, button')?.focus();
      return;
    }

    if (colonna.value) colonna.value.inert = false;
    document.body.style.removeProperty('overflow');
    // Solo se il focus e' ancora dentro il pannello che si sta chiudendo: se
    // nel frattempo e' finito altrove — per esempio perche' si e' cambiata
    // pagina — riportarlo indietro sarebbe un salto che nessuno ha chiesto.
    if (focusDaRipristinare?.isConnected && barra?.contains(document.activeElement)) {
      focusDaRipristinare.focus();
    }
    focusDaRipristinare = null;
  },
);

/**
 * Sopra la soglia da tavoletta il pannello mobile non esiste piu'.
 *
 * Senza questo, chi apre il menu sul telefono e poi gira il dispositivo — o
 * allarga la finestra sul portatile — si ritrova la barra tornata in colonna e
 * il velo rimasto sopra tutto il resto: uno strato scuro a schermo intero che
 * intercetta ogni clic e che non ha piu' nessun comando per chiudersi, perche'
 * il bottone che lo apriva e' nascosto proprio sopra i 1024 px.
 */
const schermoLargo = window.matchMedia('(min-width: 1024px)');

function suLarghezza(e: MediaQueryListEvent | MediaQueryList): void {
  if (e.matches) ui.barraMobileAperta = false;
}

/**
 * Il tasto di fuga chiude il pannello mobile.
 *
 * È l'unica sovrapposizione della cornice che non sia un componente di
 * PrimeVue, quindi è l'unica di cui dobbiamo occuparci noi. Chi la apre per
 * sbaglio deve poterla chiudere senza cercare la crocetta.
 */
function suTasto(e: KeyboardEvent): void {
  if (e.key === 'Escape' && ui.barraMobileAperta) ui.barraMobileAperta = false;
}

onMounted(() => {
  window.addEventListener('keydown', suTasto);
  schermoLargo.addEventListener('change', suLarghezza);
  suLarghezza(schermoLargo);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', suTasto);
  schermoLargo.removeEventListener('change', suLarghezza);
  // La cornice se ne va — per esempio all'uscita — mentre il pannello e'
  // aperto: senza questa riga il documento resterebbe bloccato allo
  // scorrimento anche sulla schermata d'ingresso.
  document.body.style.removeProperty('overflow');
});
</script>

<template>
  <div ref="cornice" class="cornice" :style="{ '--barra': larghezzaBarra }">
    <!--
      Primo elemento raggiungibile con il tabulatore. Per chi naviga da tastiera
      è la differenza fra arrivare al contenuto subito e attraversare venti voci
      di menu a ogni cambio di pagina.
    -->
    <!--
      `inert` anche qui, e non solo sulla colonna.

      `inert` va su `colonna`, ma questo collegamento e il velo ne sono
      **fratelli**: restavano quindi raggiungibili con il pannello mobile aperto.
      Con Maiusc+Tab dalla prima voce del menu si finiva sul salto al contenuto,
      che punta a un `#contenuto` reso inerte un istante prima — il browser non
      puo' dare il focus a un elemento inerte, quindi il fuoco cadeva sul `body`
      e la pressione successiva ripartiva dall'inizio del documento.

      È il primo dei quattro obblighi elencati poco sopra — «il focus non deve
      uscire dal pannello» — e non era mantenuto.
    -->
    <a class="beste-skip-link" href="#contenuto" :inert="ui.barraMobileAperta">Vai al contenuto</a>

    <!--
      Il velo del pannello mobile. `aria-hidden` perché è decorativo: la chiusura
      è già raggiungibile dal tasto di fuga e dal comando nella barra.

      Sta **prima** della barra, e non e' indifferente. I due elementi hanno lo
      stesso indice di sovrapposizione (vedi `.velo` piu' sotto), quindi a
      decidere chi copre chi resta l'ordine nel documento: il velo per primo, la
      barra sopra. Con l'ordine invertito — com'era — il velo finiva sopra il
      pannello che avrebbe dovuto oscurare e, essendo `position: fixed; inset: 0`,
      intercettava ogni tocco: si toccava una voce di menu e il menu si chiudeva
      invece di portare da nessuna parte.
    -->
    <Transition name="velo">
      <div
        v-if="ui.barraMobileAperta"
        class="velo"
        aria-hidden="true"
        @click="ui.barraMobileAperta = false"
      />
    </Transition>

    <AppSidebar />

    <div ref="colonna" class="colonna">
      <AppTopbar />

      <!--
        Qui e non in `App.vue`: la striscia vive **nella colonna**, sotto la barra e sopra il
        contenuto, e la colonna è anche ciò che diventa inerte con il pannello mobile aperto —
        fuori di qui resterebbe un comando raggiungibile dietro il velo. Le pagine senza
        cornice non la ricevono per scelta: all'ingresso non c'è ancora un lavoro da perdere, il
        cambio password obbligato è un gesto solo, e le presenze di linea sono una PWA a sé su
        uno schermo che serve a una cosa sola.
      -->
      <AvvisoVersioneNuova />
      <!-- Sei in videochiamata dal telefono: continua qui (2026-10-28, `AvvisoChiamataAltrove`). -->
      <AvvisoChiamataAltrove />
      <!--
        Un collega ti chiama (2026-10-28, `ChiamataInArrivo`): la finestra che squilla. Solo dove le
        videochiamate ci sono — senza motore non si chiama nessuno, e non si chiede niente.
      -->
      <ChiamataInArrivo v-if="auth.videochiamateDisponibili" />

      <main id="contenuto" ref="contenuto" class="contenuto" tabindex="-1">
        <RouterView v-slot="{ Component, route: r }">
          <Transition name="pagina" mode="out-in" @after-enter="suPaginaEntrata">
            <!--
              La chiave è il percorso **senza** la stringa di ricerca.
              Cambiare un filtro modifica l'indirizzo (§10.3) ma non è un
              cambio di schermata: se la chiave includesse la ricerca, ogni
              ordinamento di colonna smonterebbe e rimonterebbe la tabella,
              con tanto di transizione. Sarebbe un difetto vistoso proprio
              nell'operazione più frequente del pannello.
            -->
            <component :is="Component" :key="r.path" />
          </Transition>
        </RouterView>
      </main>
    </div>
  </div>
</template>

<style scoped>
.cornice {
  display: grid;
  grid-template-columns: var(--barra) minmax(0, 1fr);
  min-height: 100dvh;

  /*
   * La transizione della larghezza sta **qui**, sulla griglia, e non sulla
   * barra: così il contenuto si allarga esattamente insieme alla barra che si
   * stringe, invece di scattare quando quella ha finito.
   */
  transition: grid-template-columns var(--beste-duration-normal) var(--beste-ease-standard);
}

/* `minmax(0, 1fr)` sopra è ciò che impedisce a una tabella larga di sfondare
   la colonna: senza, `1fr` significa "almeno il contenuto", e il contenuto di
   una tabella con quindici colonne è più largo dello schermo. */

.colonna {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.contenuto {
  flex: 1;
  min-width: 0;
  padding: var(--beste-space-24) var(--beste-layout-gutterDesktop) var(--beste-space-48);
}

/* Il contenuto riceve il focus dal salto: non deve però disegnare l'anello,
   perché non è un comando. Il focus serve solo a spostare il punto di lettura. */
.contenuto:focus {
  outline: none;
}

/*
 * Il velo sta sull'indice del pannello, non su quello dei veli.
 *
 * `--beste-z-scrim` (400) e' l'indice del velo che sta sotto una **finestra
 * modale** (500): li' l'ordine e' giusto. Qui il velo appartiene al pannello
 * laterale, che vive a 300, e usare 400 lo metteva sopra il proprio pannello.
 * A pari indice decide l'ordine nel documento, e nel modello il velo viene
 * prima: e' l'unica soluzione che non costringe a inventare un livello nuovo
 * fra `drawer` e `scrim` in una scala condivisa anche con l'app.
 */
.velo {
  position: fixed;
  inset: 0;
  z-index: var(--beste-z-drawer);
  background: var(--beste-scrim);
}

.velo-enter-active,
.velo-leave-active {
  transition: opacity var(--beste-duration-fast) var(--beste-ease-standard);
}

.velo-enter-from,
.velo-leave-to {
  opacity: 0;
}

/*
 * Sotto la soglia da tavoletta la barra esce dal flusso.
 *
 * Non si *riduce*: sparisce e torna come pannello sovrapposto. Ridurla a 72 px
 * su uno schermo da 400 significherebbe togliere un quinto della larghezza a
 * una tabella che già non ci sta.
 */
@media (max-width: 1023px) {
  .cornice {
    grid-template-columns: minmax(0, 1fr);
  }

  .contenuto {
    padding: var(--beste-space-16) var(--beste-layout-gutterMobile) var(--beste-space-40);
  }
}

/*
 * La transizione `pagina` non e' qui: sta in `App.vue`, in un blocco non
 * delimitato, perche' governa **entrambi** i punti in cui si cambia schermata —
 * dentro la cornice e fuori (ingresso, cambio password obbligato). Ne esisteva
 * una seconda copia identica proprio in questo file: due sorgenti per la stessa
 * animazione sono due sorgenti da tenere allineate a mano, ed e' il modo in cui
 * le durate cominciano a divergere.
 */
</style>
